// Werke-Dialog der Space-Seite (Welle 5, V5.4, Korrekturrunde 1: Material UI).
// MUI Dialog nach dem Muster TargetNamesDialog: Das Portal liegt außerhalb von .iio, deshalb trägt
// das Paper die Klasse „iio“ und alle Farben kommen aus var(--iio-*) in sx, nie aus MUI-Defaults.
//   Tab „Werke“                  Editor der Space-Liste (GET/PUT /api/spaces/:spaceId/werke, V5.1)
//   Tab „Werks Instanz Mapping“  Slot für PlantInstanceMapping (Lane fe-mapping, V5.3)
// Die Liste gehört dem Space. Die Listen an den Instanzen bleiben stehen (Rückfall), deshalb zeigt
// der Dialog bei einer noch nicht gespeicherten Liste nur einen Vergleich und löscht nichts.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Skeleton, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
  useMediaQuery,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { api } from "../utils/helpers";
import { stageLabel } from "../utils/stages";
import PlantInstanceMapping from "./PlantInstanceMapping";
import { toRows, cleanRows, validateRows, sameRows, differingCompare, werkLabel, WERK_KENNUNG } from "./spaceWerkeModel";

// --- Stile, ausschließlich über CSS-Variablen ---------------------------------------------------
const FONT = "inherit";
const buttonSx = {
  minHeight: 40, textTransform: "none", fontFamily: FONT, fontWeight: 500, fontSize: 13, borderRadius: "6px",
  color: "var(--iio-text-1)", "&:hover": { background: "var(--iio-bg-3)" },
  "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: 2 },
  "&.Mui-disabled": { color: "var(--iio-text-4)" },
};
// boxShadow none auch im Hover: muiTheme.js gibt containedPrimary sonst einen Glow (nur lokal
// überschrieben, das Theme bleibt für die übrige App unverändert).
const primarySx = {
  ...buttonSx, background: "var(--iio-signal)", color: "var(--iio-hero-text)", boxShadow: "none",
  "&:hover": { background: "var(--iio-signal-2)", boxShadow: "none" },
  "&.Mui-disabled": { background: "var(--iio-bg-3)", color: "var(--iio-text-3)" },
};
// Ton = info | warning | critical | success. Dezente Fläche, Rahmen und Symbol in der Statusfarbe.
const alertSx = tone => ({
  fontFamily: FONT, fontSize: 13, lineHeight: 1.5, borderRadius: "8px", alignItems: "flex-start",
  color: "var(--iio-text-1)",
  background: `color-mix(in srgb, var(--iio-${tone}) 6%, var(--iio-bg-1))`,
  border: `1px solid color-mix(in srgb, var(--iio-${tone}) 35%, var(--iio-border))`,
  "& .MuiAlert-icon": { color: `var(--iio-${tone})` },
  "& .MuiAlert-message": { minWidth: 0, flex: 1 },
  "& .MuiAlert-action": { alignItems: "center", pt: 0 },
});
const fieldSx = {
  "& .MuiInputBase-root": { fontFamily: FONT, fontSize: 13, color: "var(--iio-text-1)", background: "var(--iio-bg-0)", borderRadius: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
  "& .MuiOutlinedInput-root:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-text-3)" },
  // Der Fokusring aus muiTheme.js ist blau bzw. orange fest verdrahtet; hier aus dem iio-Token.
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-brand-fg)",
    boxShadow: "0 0 0 3px color-mix(in srgb, var(--iio-brand-fg) 18%, transparent)" },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-critical)" },
  "& .MuiInputLabel-root": { fontFamily: FONT, fontSize: 13, color: "var(--iio-text-3)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--iio-brand-fg)" },
  "& .MuiInputLabel-root.Mui-error": { color: "var(--iio-critical)" },
  "& .MuiInputBase-input::placeholder": { color: "var(--iio-text-4)", opacity: 1 },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "var(--iio-text-2)" },
  "& .MuiFormHelperText-root": { fontFamily: FONT, fontSize: 12, mx: 0, color: "var(--iio-text-3)" },
  "& .MuiFormHelperText-root.Mui-error": { color: "var(--iio-critical)" },
};
const tableSx = {
  "& .MuiTableCell-root": { fontFamily: FONT, fontSize: 12, color: "var(--iio-text-1)", borderColor: "var(--iio-border)",
    px: 1, py: 0.75, verticalAlign: "top", overflowWrap: "anywhere" },
  "& .MuiTableCell-head": { fontWeight: 600, color: "var(--iio-text-3)", backgroundColor: "transparent" },
};

function ambiguousText(ambiguous) {
  if (!Array.isArray(ambiguous) || !ambiguous.length) return "";
  return ambiguous.map(a => Array.isArray(a) ? a.join(" / ")
    : (a && typeof a === "object" ? Object.values(a).flat().join(" / ") : String(a))).join(", ");
}

function Vergleich({ eintraege, t, reducedMotion }) {
  return (
    <Accordion disableGutters elevation={0} TransitionProps={{ timeout: reducedMotion ? 0 : undefined, unmountOnExit: true }}
      sx={{ mt: 1, background: "transparent", color: "inherit", border: "1px solid var(--iio-border)", borderRadius: "6px !important",
        "&::before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreRounded sx={{ color: "var(--iio-text-3)" }}/>}
        aria-controls="iio-space-werke-vergleich" id="iio-space-werke-vergleich-kopf"
        sx={{ minHeight: 40, px: 1.5, fontFamily: FONT, fontSize: 13, "& .MuiAccordionSummary-content": { my: 1 },
          "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", background: "transparent" } }}>
        {t("spaceWerke.uebernahme.vergleich", { count: eintraege.length })}
      </AccordionSummary>
      <AccordionDetails id="iio-space-werke-vergleich" sx={{ px: 1.5, pt: 0, pb: 1.5, overflowX: "auto" }}>
        <Table size="small" sx={tableSx} aria-labelledby="iio-space-werke-vergleich-kopf">
          <TableHead>
            <TableRow>
              <TableCell>{t("spaceWerke.uebernahme.spalteInstanz")}</TableCell>
              <TableCell>{t("spaceWerke.uebernahme.spalteStufe")}</TableCell>
              <TableCell>{t("spaceWerke.uebernahme.nurDort")}</TableCell>
              <TableCell>{t("spaceWerke.uebernahme.fehltDort")}</TableCell>
              <TableCell>{t("spaceWerke.uebernahme.andererName")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eintraege.map(c => (
              <TableRow key={c.instanceId}>
                <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{c.instanceName || c.instanceId}</TableCell>
                <TableCell>{c.stage ? stageLabel(c.stage) : ""}</TableCell>
                <TableCell>{(c.onlyHere || []).map(werkLabel).join(", ")}</TableCell>
                <TableCell>{(c.missing || []).map(werkLabel).join(", ")}</TableCell>
                <TableCell>
                  {(c.renamed || []).map(r => (
                    <Box key={r.kennung}>{t("spaceWerke.uebernahme.umbenannt", { kennung: r.kennung, here: r.here, space: r.space })}</Box>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

export default function SpaceWerkeDialog({ space, data, loading, loadError, sourceName, onReload, onSaved, onClose }) {
  const { t } = useTranslation(["space", "gemeinsam"]);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // Die Zeichenfläche des Mappings braucht rund 960 px (Integration I5): Dialog bis lg, darunter Vollbild.
  const fullScreen = useMediaQuery("(max-width: 1023.95px)");
  const [tab, setTab] = useState("werke");
  const [rows, setRows] = useState(() => toRows(data?.werke));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedOk, setSavedOk] = useState(false);
  const [reloadAfterSaveFailed, setReloadAfterSaveFailed] = useState(false);
  const [closeWarn, setCloseWarn] = useState(false);
  const [fremd, setFremd] = useState(false);
  // Ungespeicherter Entwurf im Mapping (PlantInstanceMapping meldet ihn über onDirtyChange).
  const [mappingDirty, setMappingDirty] = useState(false);
  const [mappingBesucht, setMappingBesucht] = useState(false);
  // Fußplätze für das Mapping (Nachtrag I5): Zähler und Ansage links, Verwerfen und Speichern neben „Schließen“.
  const [mapStatusEl, setMapStatusEl] = useState(null);
  const [mapActionsEl, setMapActionsEl] = useState(null);
  const mappingFooter = useMemo(() => (mapStatusEl && mapActionsEl ? { status: mapStatusEl, actions: mapActionsEl } : null), [mapStatusEl, mapActionsEl]);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Neue Serverdaten setzen den Editor zurück. Ein erneutes Laden mit unverändertem Stand lässt
  // laufende Eingaben stehen. Hat jemand anderes inzwischen geändert und lagen eigene Eingaben
  // vor, sagt ein Hinweis das, statt still zurückzusetzen.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const basis = useRef(data?.werke);
  const stand = JSON.stringify([data?.source ?? null, data?.revision ?? null, data?.werke ?? null]);
  useEffect(() => {
    const neu = toRows(data?.werke);
    const eigeneEingaben = !sameRows(rowsRef.current, toRows(basis.current));
    if (eigeneEingaben && !sameRows(rowsRef.current, neu)) setFremd(true);
    basis.current = data?.werke;
    setRows(neu);
    setShowErrors(false);
    setConflict(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stand]);

  const editable = data?.editable === true;
  const ausInstanz = data?.source === "instance";
  const abweichend = useMemo(() => differingCompare(data?.compare), [data]);
  const pruefung = useMemo(() => validateRows(rows), [rows]);
  const geaendert = !!data && !sameRows(rows, toRows(data?.werke));
  const ungespeichert = (editable && geaendert) || mappingDirty;
  const kannSpeichern = !!data && editable && !saving && (ausInstanz || geaendert);
  // be-werke M2: Eine leere Space-Liste wird nicht auf die QAS-Instanz zurückgeschrieben (keine Instanz wird geleert).
  const leereListe = !!data && editable && cleanRows(rows).length === 0 && (kannSpeichern || savedOk);
  // be-werke M6: Ohne Kennung an der Instanz schlägt der Vorschlag den Namen vor; mit Leerzeichen ist er kein gültiger Bezeichner.
  const altnamen = useMemo(() => (ausInstanz ? toRows(data?.werke).filter(w => w.kennung && !WERK_KENNUNG.test(w.kennung)) : []), [ausInstanz, data]);

  const bearbeitet = () => { setSavedOk(false); setFremd(false); setCloseWarn(false); setReloadAfterSaveFailed(false); };
  const aendern = (idx, feld, wert) => { bearbeitet(); setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [feld]: wert } : r))); };
  const entfernen = idx => { bearbeitet(); setRows(prev => prev.filter((_, i) => i !== idx)); };
  const hinzufuegen = () => { bearbeitet(); setRows(prev => [...prev, { name: "", kennung: "" }]); };

  // Hintergrund und Escape schließen nicht, solange Eingaben ungespeichert sind. Der Knopf
  // „Schließen“ ist eine ausdrückliche Entscheidung und schließt immer.
  const handleClose = (_event, reason) => {
    if ((reason === "backdropClick" || reason === "escapeKeyDown") && ungespeichert) { setCloseWarn(true); return; }
    onClose();
  };

  const neuLaden = async () => {
    setSavedOk(false);
    setReloadAfterSaveFailed(false);
    const frisch = await onReload?.();
    if (!mounted.current || !frisch) return;
    setConflict(false);
    setSaveError(null);
    setShowErrors(false);
    setFremd(false);
    basis.current = frisch.werke;
    setRows(toRows(frisch.werke));
  };

  const speichern = async () => {
    setShowErrors(true);
    setSavedOk(false);
    setSaveError(null);
    setConflict(false);
    setFremd(false);
    setCloseWarn(false);
    setReloadAfterSaveFailed(false);
    if (!pruefung.ok) return;
    setSaving(true);
    try {
      // Erste Übernahme: revision null. Danach die geladene revision (optimistische Sperre).
      const revision = ausInstanz ? null : (data?.revision ?? null);
      const res = await api(`/api/spaces/${space.id}/werke`, { method: "PUT", body: JSON.stringify({ werke: cleanRows(rows), revision }) });
      if (!mounted.current) return;
      // Die Antwort sofort übernehmen: Scheitert das Nachladen, steht trotzdem die neue revision
      // im Dialog. Ein zweites Speichern schickt dann nicht erneut revision null (Folge-409).
      onSaved?.(res);
      setSavedOk(true);
      setShowErrors(false);
      const frisch = await onReload?.();
      if (mounted.current && !frisch) setReloadAfterSaveFailed(true);
    } catch (e) {
      if (!mounted.current) return;
      if (e?.status === 409 || e?.code === "SPACE_WERKE_CONFLICT") setConflict(true);
      else setSaveError({ message: e?.message || t("gemeinsam:zustand.fehler"), ambiguous: e?.details?.ambiguous });
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const fehlerText = f => (f ? t(`spaceWerke.fehler.${f.code}`, f) : undefined);

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" fullWidth fullScreen={fullScreen} scroll="paper" aria-labelledby="iio-space-werke-titel"
      transitionDuration={reducedMotion ? 0 : undefined}
      PaperProps={{ className: "iio", "data-fullscreen": fullScreen ? "true" : undefined, sx: {
        ...(fullScreen ? { m: 0, width: "100%", maxHeight: "none", borderRadius: 0, border: 0 }
          : { m: 3, width: "calc(100% - 48px)", maxHeight: "calc(100dvh - 32px)", border: "1px solid var(--iio-border)", borderRadius: "12px" }),
        boxShadow: "var(--iio-shadow-md)",
        fontFamily: '"Source Sans 3", sans-serif', color: "var(--iio-text-1)", background: "var(--iio-bg-1)", backgroundImage: "none",
      } }}>
      {/* DialogTitle übernimmt die id aus aria-labelledby des Dialogs (DialogContext). Er ist deshalb
          selbst das h2 und enthält nur den Titel; Space-Name und Schließen stehen daneben. */}
      <Typography component="div" sx={{ px: { xs: 2, sm: 3 }, pt: 2, pr: 7, fontFamily: FONT, fontSize: 12, color: "var(--iio-text-3)", overflowWrap: "anywhere" }}>
        {space.name}
      </Typography>
      <DialogTitle component="h2" sx={{ px: { xs: 2, sm: 3 }, pt: 0, pb: 1, pr: 7, m: 0, fontFamily: FONT, fontSize: 20, fontWeight: 600, color: "var(--iio-text-1)" }}>
        {t("spaceWerke.dialog.titel")}
      </DialogTitle>
      <Tooltip title={t("gemeinsam:aktion.schliessen")}>
        <IconButton onClick={onClose} aria-label={t("gemeinsam:aktion.schliessen")}
          sx={{ position: "absolute", right: 12, top: 12, color: "var(--iio-text-3)", "&:hover": { background: "var(--iio-bg-3)" },
            "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)" } }}>
          <CloseRounded fontSize="small"/>
        </IconButton>
      </Tooltip>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === "mapping") setMappingBesucht(true); }} aria-label={t("spaceWerke.dialog.tabsLabel")}
        TabIndicatorProps={{ sx: { background: "var(--iio-brand-fg)", boxShadow: "none", ...(reducedMotion && { transition: "none" }) } }}
        sx={{ px: { xs: 1, sm: 2 }, minHeight: 40, borderBottom: "1px solid var(--iio-border)",
          "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontFamily: FONT, fontSize: 13, fontWeight: 500, color: "var(--iio-text-2)" },
          "& .MuiTab-root.Mui-selected": { color: "var(--iio-text-1)" },
          "& .MuiTab-root.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: -2 } }}>
        <Tab value="werke" label={t("spaceWerke.dialog.tabWerke")} id="iio-space-werke-tab-werke" aria-controls="iio-space-werke-panel-werke" disableRipple/>
        <Tab value="mapping" label={t("spaceWerke.dialog.tabMapping")} id="iio-space-werke-tab-mapping" aria-controls="iio-space-werke-panel-mapping" disableRipple/>
      </Tabs>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderColor: "var(--iio-border)" }}>
        {/* Rückfrage über beiden Tabs, damit sie auch im Mapping sichtbar ist. */}
        {closeWarn && (
          <Alert severity="warning" sx={{ ...alertSx("warning"), mb: 1.5 }} action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => setCloseWarn(false)} sx={buttonSx}>{t("spaceWerke.editor.weiterBearbeiten")}</Button>
              <Button size="small" onClick={onClose} sx={{ ...buttonSx, color: "var(--iio-critical)" }}>{t("spaceWerke.editor.verwerfen")}</Button>
            </Stack>
          }>{t("spaceWerke.editor.ungespeichert")}</Alert>
        )}
        {tab === "werke" && (
          <Stack role="tabpanel" id="iio-space-werke-panel-werke" aria-labelledby="iio-space-werke-tab-werke" spacing={1.5}>
            {loading && !data && !loadError && (
              <Stack spacing={1} aria-busy="true" aria-label={t("spaceWerke.editor.laedt")}>
                {[0, 1, 2].map(i => (
                  <Skeleton key={i} variant="rounded" height={40} animation={reducedMotion ? false : "pulse"} sx={{ bgcolor: "var(--iio-bg-3)" }}/>
                ))}
              </Stack>
            )}

            {loadError && !reloadAfterSaveFailed && (
              <Alert severity="error" sx={alertSx("critical")}
                action={<Button size="small" onClick={neuLaden} sx={buttonSx}>{t("spaceWerke.editor.neuLaden")}</Button>}>
                {t("spaceWerke.kachel.ladeFehler")}
              </Alert>
            )}

            {fremd && (
              <Alert severity="info" role="status" sx={alertSx("info")} onClose={() => setFremd(false)}>{t("spaceWerke.editor.fremdGeaendert")}</Alert>
            )}

            {data && ausInstanz && (
              <Alert severity="info" role="status" sx={alertSx("info")}>
                <Typography component="div" sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                  {sourceName ? t("spaceWerke.uebernahme.titel", { name: sourceName }) : t("spaceWerke.uebernahme.titelOhneName")}
                </Typography>
                <Box>{sourceName ? t("spaceWerke.uebernahme.text", { name: sourceName }) : t("spaceWerke.uebernahme.textOhneName")}</Box>
                {altnamen.length > 0 && <Box sx={{ mt: 0.75 }} data-testid="space-werke-altname">{t("spaceWerke.uebernahme.altname", { beispiel: altnamen[0].kennung })}</Box>}
                {abweichend.length > 0
                  ? <Vergleich eintraege={abweichend} t={t} reducedMotion={reducedMotion}/>
                  : <Box sx={{ mt: 0.75, color: "var(--iio-text-3)" }}>{t("spaceWerke.uebernahme.keineAbweichung")}</Box>}
              </Alert>
            )}

            {data && <>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, lineHeight: 1.5, color: "var(--iio-text-2)" }}>{t("spaceWerke.editor.info")}</Typography>
              {rows.length === 0 && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "var(--iio-text-3)" }}>{t("spaceWerke.editor.leer")}</Typography>
              )}
              {rows.map((w, idx) => {
                const f = showErrors ? pruefung.rows[idx] : null;
                const nr = idx + 1;
                const entfernenLbl = t("spaceWerke.editor.entfernen", { nr });
                return (
                  <Box key={idx} sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr) 40px", sm: "minmax(0,1fr) 160px 40px" },
                    gap: 1, alignItems: "start" }}>
                    <TextField size="small" fullWidth value={w.name} disabled={!editable} sx={fieldSx}
                      label={t("spaceWerke.editor.name")} placeholder={t("spaceWerke.editor.namePlatzhalter")}
                      inputProps={{ "aria-label": t("spaceWerke.editor.zeile", { feld: t("spaceWerke.editor.name"), nr }) }}
                      error={!!f?.name} helperText={fehlerText(f?.name)}
                      onChange={e => aendern(idx, "name", e.target.value)}/>
                    <TextField size="small" fullWidth value={w.kennung} disabled={!editable}
                      sx={{ ...fieldSx, gridColumn: { xs: "1", sm: "auto" }, gridRow: { xs: "2", sm: "auto" } }}
                      label={t("spaceWerke.editor.kennung")} placeholder={t("spaceWerke.editor.kennungPlatzhalter")}
                      inputProps={{ "aria-label": t("spaceWerke.editor.zeile", { feld: t("spaceWerke.editor.kennung"), nr }) }}
                      error={!!f?.kennung} helperText={fehlerText(f?.kennung)}
                      onChange={e => aendern(idx, "kennung", e.target.value)}/>
                    {editable ? (
                      <Tooltip title={entfernenLbl}>
                        <IconButton aria-label={entfernenLbl} onClick={() => entfernen(idx)}
                          sx={{ width: 40, height: 40, borderRadius: "6px", border: "1px solid var(--iio-border)", color: "var(--iio-text-2)",
                            gridColumn: { xs: "2", sm: "auto" }, gridRow: { xs: "1", sm: "auto" },
                            "&:hover": { color: "var(--iio-critical)", background: "var(--iio-bg-3)" },
                            "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)" } }}>
                          <DeleteOutlineRounded fontSize="small"/>
                        </IconButton>
                      </Tooltip>
                    ) : <span aria-hidden="true"/>}
                  </Box>
                );
              })}
              {editable && (
                <Box>
                  <Button variant="outlined" startIcon={<AddRounded/>} onClick={hinzufuegen}
                    sx={{ ...buttonSx, borderColor: "var(--iio-border)", borderStyle: "dashed", color: "var(--iio-text-2)",
                      "&:hover": { borderColor: "var(--iio-text-3)", borderStyle: "dashed", background: "var(--iio-bg-3)", color: "var(--iio-text-1)" } }}>
                    {t("spaceWerke.editor.hinzufuegen")}
                  </Button>
                </Box>
              )}
              {!editable && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "var(--iio-text-3)" }}>{t("spaceWerke.editor.nurLesen")}</Typography>
              )}
            </>}

            {leereListe && <Alert severity="info" role="status" sx={alertSx("info")} data-testid="space-werke-leer-hinweis">{t("spaceWerke.editor.leereListe")}</Alert>}
            {showErrors && pruefung.list && <Alert severity="error" sx={alertSx("critical")}>{fehlerText(pruefung.list)}</Alert>}
            {showErrors && !pruefung.ok && !pruefung.list && <Alert severity="error" sx={alertSx("critical")}>{t("spaceWerke.fehler.pruefen")}</Alert>}
            {conflict && (
              <Alert severity="warning" sx={alertSx("warning")}
                action={<Button size="small" onClick={neuLaden} sx={buttonSx}>{t("spaceWerke.editor.neuLaden")}</Button>}>
                {t("spaceWerke.editor.konflikt")}
              </Alert>
            )}
            {saveError && (
              <Alert severity="error" sx={alertSx("critical")}>
                {saveError.message}
                {ambiguousText(saveError.ambiguous) && <Box>{t("spaceWerke.fehler.mehrdeutig", { liste: ambiguousText(saveError.ambiguous) })}</Box>}
              </Alert>
            )}
            {savedOk && <Alert severity="success" role="status" sx={alertSx("success")}>{t("spaceWerke.editor.gespeichert")}</Alert>}
            {reloadAfterSaveFailed && (
              <Alert severity="warning" sx={alertSx("warning")}
                action={<Button size="small" onClick={neuLaden} sx={buttonSx}>{t("spaceWerke.editor.neuLaden")}</Button>}>
                {t("spaceWerke.editor.ladeFehlerNachSpeichern")}
              </Alert>
            )}
          </Stack>
        )}

        {/* Das Mapping bleibt nach dem ersten Öffnen gemountet und wird nur versteckt, sonst ginge
            sein Entwurf beim Tabwechsel verloren und onDirtyChange liefe ins Leere. */}
        {mappingBesucht && (
          <Box role="tabpanel" id="iio-space-werke-panel-mapping" aria-labelledby="iio-space-werke-tab-mapping" hidden={tab !== "mapping"}>
            {/* Rechte des Mappings aus der Route (CI/CD Verwaltungsrecht), nicht aus dem Werke-Recht: siehe PlantInstanceMapping. */}
            <PlantInstanceMapping spaceId={space.id} onDirtyChange={setMappingDirty} footer={mappingFooter}/>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1, flexWrap: "wrap", borderTop: "1px solid var(--iio-border)" }}>
        {/* Mapping-Tab: Zähler und Ansage (aria-live) links, Verwerfen und Speichern rechts neben „Schließen“. Die Plätze
            bleiben gemountet (das Mapping bleibt es auch) und sind im Tab „Werke“ nur ausgeblendet. */}
        <Box ref={setMapStatusEl} data-testid="space-werke-mapping-status"
          sx={{ display: tab === "mapping" ? "flex" : "none", flex: "1 1 auto", minWidth: 0, mr: "auto", alignItems: "center" }}/>
        <Button autoFocus onClick={onClose} sx={buttonSx}>{t("gemeinsam:aktion.schliessen")}</Button>
        <Box ref={setMapActionsEl} data-testid="space-werke-mapping-actions"
          sx={{ display: tab === "mapping" ? "flex" : "none", gap: 1, alignItems: "center", flexWrap: "wrap" }}/>
        {tab === "werke" && editable && (
          <Button variant="contained" disableElevation onClick={speichern} disabled={!kannSpeichern} sx={primarySx}>
            {saving ? t("spaceWerke.editor.speichert") : ausInstanz ? t("spaceWerke.editor.uebernehmen") : t("gemeinsam:aktion.speichern")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
